import PropTypes from 'prop-types';
import React, {Component} from 'react';
import {matchPath, withRouter} from 'react-router-dom';
import events from '@republic/foundation/events';
import {each, reduce} from '@republic/foundation/lang/array';
import {Collection} from '@republic/foundation/structures/collection';
import {createComponent, createNamedContext} from '@republic/react-foundation';

const
    matchLocation = (location, {path, exact, strict, sensitive}) => (
        path ?
            matchPath(location.pathname, {path, exact, strict, sensitive}) :
            null),

    Lazy = (
        createNamedContext(
            'Lazy',
            {register: PropTypes.func.isRequired})),

    LazySwitch = (
        withRouter(
            createComponent(
                'LazySwitch',
                {
                    propTypes: {
                        location: PropTypes.object.isRequired
                    }
                },
                class LazySwitch extends Component {
                    constructor(props) {
                        super(props);

                        const publisher = events('loading');

                        this.mounted = false;
                        this.publish = publisher.publish;
                        this.subscribe = publisher.subscribe;
                        this.loading = null;
                        this.routes = new Collection();
                        this.provider = {
                            listen: this.listen,
                            register: this.register
                        };
                    }

                    listen = listener => {
                        if (this.loading) {
                            listener(this.loading);
                        }
                        return this.subscribe.loading(listener);
                    }

                    register = (props, load, matched) => {
                        const
                            entry = (
                                this.routes.add({
                                    props,
                                    load,
                                    matched
                                }));

                        if (this.mounted) {
                            this.update();
                        }
                        return () => {
                            entry.remove();
                        };
                    }

                    update() {
                        let matches = 0;
                        const
                            {location} = this.props,
                            pending = [];

                        this.routes.each(route => {
                            const props = route.props();

                            if (props.path) {
                                const match = matchLocation(location, props);

                                if (match) {
                                    matches += 1;
                                }
                                pending.push({
                                    load: route.load,
                                    matched: route.matched,
                                    match
                                });
                            }
                        });
                        this.routes.each(route => {
                            const
                                props = route.props(),
                                {match} = this.props;

                            if (!props.path) {
                                pending.push({
                                    load: route.load,
                                    matched: route.matched,
                                    match: matches ? null : match
                                });
                            }
                        });
                        if (pending.length) {
                            const
                                loading = (
                                    Promise.all(
                                        reduce(
                                            pending,
                                            (promises, route) => {
                                                if (route.match) {
                                                    promises.push(
                                                        Promise.resolve(
                                                            route.load()));
                                                }
                                                return promises;
                                            },
                                            []))
                                    .then(() => {
                                        if (this.mounted && this.loading === loading) {
                                            each(pending, route => {
                                                route.matched(route.match);
                                            });
                                        }
                                    }));

                            this.loading = loading;
                            this.publish.loading(loading);
                        }
                    }

                    componentDidMount() {
                        this.mounted = true;
                        this.update();
                    }

                    componentDidUpdate() {
                        this.update();
                    }

                    componentWillUnmount() {
                        this.mounted = false;
                    }

                    render() {
                        const {children} = this.props;

                        return (
                            <Lazy.Provider value={this.provider}>
                                {children}
                            </Lazy.Provider>);
                    }
                })));

LazySwitch.context = Lazy;

export default LazySwitch;
